<template lang="pug">
  .grouped
    .as-config(v-if="product.rhpaDisplayType === 'swatch'")
      .swatch-group(v-for="swatchGroup in simpleSwatchGroups")
        Swatches.group-swatches(
          v-if="buyRequest.children[0]"
          :modelValue="buyRequest.children[0].productId"
          @update:modelValue="updateChild($event)"
          :required="true"
          :options="swatchGroup.opts"
          :label="swatchGroup.label"
        )
        configure-buy-request(:buyRequest="buyRequest.children[0]" v-if="swatchGroup.isActive")
    .normal.view-all(v-else)
      template(v-for="(child, i) in buyRequest.children")
        template(
          v-if="isAccessoriesLayout && isMainAccessoryProduct(child.productId, i)"
        )
          rhpa-body.option.grouped-rhpa(
            :buyRequest="child"
            :groupMeta="meta"
            :key="i"
          )
          rhpa-footer(
            :buy-request="buyRequest"
            :disabled="addToCartDisabled"
            :hideWishlist="true"
            :buttonOnly="true"
          )
          h2.accessories-title.heading-h4 {{ __(product.name) + ' ' + __('Accessories')}}
            .accessory-count {{ __('Available Options') + ' (' + (buyRequest.children.length - 1).toString() + ')' }}
          .divider
        template(v-else)
          rhpa-body.option.grouped-rhpa(
            :buyRequest="child"
            :groupMeta="meta"
            :layout="isAccessoriesLayout ? 'accessory' : 'default'"
            :key="i"
          )
</template>
<script>
import { defineAsyncComponent } from 'vue'
import RhpaFooter from './rhpa-footer.vue'
import { useProductStore } from '@/js/stores/productStore'
import stockStatus          from '@/js/helpers/stockStatus'
import RhpaBody             from '@/js/components/rhpa/rhpa-body.vue'
import { createBuyRequest, getBuyRequestMeta } from '@/js/helpers/buy-request'

export default {
  name       : 'GroupedParent',
  components : { 
    RhpaBody, 
    RhpaFooter: defineAsyncComponent(() => import('./rhpa-footer.vue')) },
  props: {
    buyRequest: {
      type     : Object,
      required : true,
    },
    addToCartDisabled: {
      type    : Boolean,
      default : true,
    },
  },

  setup() {
    const productStore = useProductStore()

    return { productStore }
  },

  data: () => ({
    meta: null,
  }),

  computed: {
    products() {
      return this.productStore.products
    },

    product() {
      return this.products[this.buyRequest.productId]
    },

    isAccessoriesLayout() {
      return this.product.roguebuilder?.props?.isAccessoryProduct
    },

    simpleSwatchGroups() {
      if (!this.product?.childrenIds?.length) {
        return []
      }

      const children = this.product.childrenIds
        .map(id => this.products[id])
        .filter(prod => Boolean(prod))

      if (!children || children.length === 0) {
        return []
      }

      let removeFromEnd = ''
      let removeFromStart = ''
      const childNameSplit = children[0].name.split(' ')

      for (let i = childNameSplit.length; i > 0; i -= 1) {
        const part = childNameSplit.slice(i).join(' ')
        if (children.every(c => c.name.endsWith(part))) {
          removeFromEnd = part
        }
      }

      if (removeFromEnd.split(' ').length === 1) {
        removeFromEnd = ''
      }

      for (let i = 0; i < childNameSplit.length; i += 1) {
        const part = childNameSplit.slice(0, i).join(' ')
        if (children.every(c => c.name.startsWith(part))) {
          removeFromStart = part
        }
      }

      if (removeFromStart.split(' ').length === 1 && removeFromStart !== 'Rogue') {
        removeFromStart = ''
      }

      removeFromEnd = removeFromEnd.replace(/(pair|set|kgs|lbs|kg|lb)/gi, '')
      removeFromStart = removeFromStart
        .replace('Compex SP', 'Compex ')
        .replace(/(pair|set|kgs|lbs|kg|lb)/gi, '')

      const opts = children.filter(c => Boolean(c))
        // Leaving this commented out in case we need to go back and undo.
        // This sort fixed sizes showing out of order.
        // It seems the child products are coming in from the backend in the correct order now.
        // .sort((a, b) => a.price - b.price)
        .map(c => {
          let label = c.attributes.rogue_swatch_label || c.name.replace(removeFromEnd, '').replace(removeFromStart, '') || ''
          if (label?.trim() === '') {
            label = c.name
          }

          label = label
            .replace('Echo Slam Ball', '')
            .replace('Rogue HG Bumper 2.0 - ', '')
            .replace(/Rogue Color Echo Bumper Plate Pair - (.*)/, '$1 Pair')
            .replace(/(.*) Color Training Plate 2.0 Pair \(IWF\)/, '$1 Pair (IWF)')
            .replace(/^ -/, '')

          return ({
            label,
            group  : c.attributes.rogue_swatch_group_name || 'Options',
            status : stockStatus.forProduct(c),
            value  : c.productId,
          })
        })

      const groups = {}

      opts.forEach(opt => {
        if (!groups[opt.group]) {
          groups[opt.group] = []
        }
        groups[opt.group].push(opt)
      })

      return Object.keys(groups).map(groupName => ({
        label    : groupName,
        opts     : groups[groupName],
        isActive : groups[groupName].some(opt => opt.value === this.buyRequest.children?.[0]?.productId),
      }))
    },
  },

  watch: {
    buyRequest: {
      deep: true,
      handler() {
        this.load()
      },
    },
  },

  created() {
    this.load()
  },

  methods: {
    updateChild(productId) {
      const newChild = createBuyRequest(this.products[productId], this.$rogue)
      const { metadata, qty } = this.buyRequest.children[0]
      Object.assign(newChild, { metadata, qty })
      this.buyRequest.children[0] = newChild
    },
    load() {
      this.meta = getBuyRequestMeta(this.buyRequest, this.$rogue)
    },
    isMainAccessoryProduct(childId, index) {
      return this.product.attributes.fmg_used_for_price ? this.product.attributes.fmg_used_for_price === `${childId}` : index === 0
    },
  },
}
</script>
<style lang="scss">
.rhpa:not(.force-mobile) {
  .grouped .normal {
    @include not-mobile() {
      .view-children {
        display: none;
      }
      .hide-more {
        display: block;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
.group-swatches {
  margin-bottom: $space-s;
}

.grouped-rhpa:deep(.configure) {
  margin-top: $space-s;

  .swatches-one-line .swatch {
    margin-bottom: 0;
  }
}

.view-children {
  margin-top: $space-s;
}

.hide-more {
  display: none;
}

.view-all .hide-more {
  display: block;
}

// The border expands, so we need to shrink the padding
// @todo move to SISU
.view-children:hover {
  padding: 11px 15px;
}

.see-btn {
  font-size: $size-s;
  font-weight: $weight-bold;
  text-transform: uppercase;
  font: $font-condensed;
  display: flex;
  justify-content: center;
  align-items: center;

  .icon {
    margin-left: $space-xxs;
    width: $size-xxs;
  }
}

.accessories-title {
  font-family: $font-condensed;
  font-size: $size-s;
  margin-top: $space-m;
  margin-bottom: $space-xxs;

  @include mobile() {
    font-size: $size-xs;
  }
}

.accessory-count {
  font-family: $font-base;
  text-transform: none;
  color: $color-gray-x-dark;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  display: inline-block;
  margin-left: $space-xxs;
}

.divider {
  height: 1px;
  background-color: $color-gray;
  margin: 8 0 $size-s;
}
</style>
